/**
 * Phase 17.0 — Stripe Connect return page.
 *
 * Stripe redirects the creator here after they finish (or quit) the
 * hosted onboarding flow. We re-sync the account status from Stripe and
 * route them back to /payouts with a toast.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

export default function StripeConnectReturnPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/stripe/connect/status");
        if (cancelled) return;
        setStatus(r.data);
        if (r.data?.stripe_connect_status === "ready") {
          toast.success("Stripe account connected — you're ready for payouts!");
        } else if (r.data?.stripe_connect_status === "onboarding_incomplete") {
          toast.info("Almost there — a few steps still pending in Stripe.");
        } else if (r.data?.stripe_connect_status === "restricted") {
          toast.warning("Stripe needs more info before payouts can run.");
        }
      } catch {
        if (!cancelled) toast.error("Couldn't load Stripe status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0b0b13] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-white/5 p-6 text-center"
           data-testid="stripe-connect-return-page">
        <div className="text-5xl mb-3">💳</div>
        <h1 className="text-2xl font-black mb-2">Stripe Onboarding</h1>
        {loading ? (
          <p className="text-gray-400 text-sm">Checking your account status…</p>
        ) : (
          <>
            <p className="text-sm text-gray-300 mb-1">
              Status: <strong>{status?.stripe_connect_status || "unknown"}</strong>
            </p>
            {status?.stripe_connect_status === "ready" && (
              <p className="text-xs text-emerald-300 mb-4" data-testid="stripe-return-ready-msg">
                ✓ You&apos;re all set. Approved payouts will land in your bank automatically.
              </p>
            )}
            {status?.stripe_connect_status === "onboarding_incomplete" && (
              <p className="text-xs text-amber-200 mb-4" data-testid="stripe-return-incomplete-msg">
                Stripe still needs a few details. Open the Payouts page to resume.
              </p>
            )}
            {status?.stripe_connect_status === "restricted" && (
              <p className="text-xs text-rose-200 mb-4" data-testid="stripe-return-restricted-msg">
                Your account needs additional information. Resolve from the Payouts page.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
              <Button onClick={() => navigate("/payouts")}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
                      data-testid="stripe-return-goto-payouts-btn">
                Go to Payouts
              </Button>
              <Button onClick={() => navigate("/producer")} variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="stripe-return-goto-producer-btn">
                Go to Producer
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
